import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, Loader2, ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react';
import { supabase } from '../../../src/lib/supabase';
import { useToast } from '../../../src/contexts/ToastContext';
import { RateioUnidadeGeradora } from '../../../types';
import { classificarDiaLeitura } from './consumoHistorico';
import { registrarTransferenciaAncora } from './ancoraTransferenciaActions';

interface UcResultado {
  id: number;
  numero_uc: string;
  nome_cliente: string | null;
}

interface TransferenciaTitularidadeWizardProps {
  ug: RateioUnidadeGeradora;
  saldoDisponivelKwh: number;
  onClose: () => void;
  onConfirmado: () => void;
}

const STEPS = ['Selecionar UC de destino', 'Definir montante', 'Agendar retorno'];

const DIAS_SEMANA = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

type CelulaGrade = { date: Date; foraDoMes: boolean };

/** Grid de 42 células (6 semanas), com o Date real de cada uma — inclusive
 * dias de borda dos meses vizinhos, necessários pra classificar corretamente
 * (Seção 14 — calendário de risco) e pra virar o valor de `dataRetorno` ao
 * clicar. Semana começa no domingo (offset = getDay()). */
function gerarGradeDoMes(ano: number, mesIndex: number): CelulaGrade[] {
  const primeiroDia = new Date(ano, mesIndex, 1);
  const offset = primeiroDia.getDay();
  const inicio = new Date(ano, mesIndex, 1 - offset);
  const dias: CelulaGrade[] = [];
  for (let i = 0; i < 42; i++) {
    const date = new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate() + i);
    dias.push({ date, foraDoMes: date.getMonth() !== mesIndex });
  }
  return dias;
}

const dataParaIso = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

/**
 * Wizard de Transferência de Titularidade (Seção 5, Tela 3). O calendário do
 * passo 3 é real (Seção 14): dias perto do dia de leitura da distribuidora
 * (dia_leitura_padrao) ficam marcados como risco/leitura. "Confirmar" grava
 * a transferência de verdade (rateio_ancora_transferencias) via
 * registrar_transferencia_ancora — validado no servidor contra o saldo
 * disponível real (saldo da Âncora menos o que já está emprestado).
 */
const TransferenciaTitularidadeWizard: React.FC<TransferenciaTitularidadeWizardProps> = ({
  ug,
  saldoDisponivelKwh,
  onClose,
  onConfirmado,
}) => {
  const { addToast } = useToast();
  const [step, setStep] = useState(0);

  const [termo, setTermo] = useState('');
  const [resultados, setResultados] = useState<UcResultado[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [ucDestino, setUcDestino] = useState<UcResultado | null>(null);

  const [montanteKwh, setMontanteKwh] = useState('');

  const hoje = new Date();
  const [mesCalendario, setMesCalendario] = useState(hoje.getMonth());
  const [anoCalendario, setAnoCalendario] = useState(hoje.getFullYear());
  const [dataRetorno, setDataRetorno] = useState('');
  const [salvando, setSalvando] = useState(false);

  const diaLeituraPadrao = ug.distribuidora_config?.dia_leitura_padrao;

  useEffect(() => {
    if (termo.trim().length < 2) {
      setResultados([]);
      return;
    }
    const timeout = setTimeout(async () => {
      setBuscando(true);
      try {
        const termoLike = `%${termo.trim()}%`;
        const { data, error } = await supabase
          .from('unidades_consumidoras')
          .select('id, numero_uc, nome_cliente')
          .or(`numero_uc.ilike.${termoLike},nome_cliente.ilike.${termoLike}`)
          .neq('situacao', 'Cancelada')
          .limit(20);
        if (error) throw error;
        setResultados((data || []) as UcResultado[]);
      } catch (error) {
        console.error('Error searching UCs for transferência', error);
      } finally {
        setBuscando(false);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [termo]);

  const gradeDoMes = gerarGradeDoMes(anoCalendario, mesCalendario);
  const nomeMes = new Date(anoCalendario, mesCalendario, 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  const statusDataEscolhida = dataRetorno
    ? classificarDiaLeitura(new Date(`${dataRetorno}T00:00:00`), diaLeituraPadrao)
    : 'normal';

  const podeAvancar = step === 0 ? !!ucDestino : step === 1 ? parseFloat(montanteKwh.replace(',', '.')) > 0 : true;

  const handleConfirmar = async () => {
    if (!ucDestino || !montanteKwh || !dataRetorno) {
      addToast('Preencha todos os passos antes de confirmar.', 'error');
      return;
    }
    const montante = parseFloat(montanteKwh.replace(',', '.'));
    setSalvando(true);
    try {
      await registrarTransferenciaAncora({
        rateioUnidadeGeradoraId: ug.id,
        unidadeConsumidoraDestinoId: ucDestino.id,
        montanteKwh: montante,
        dataRetornoAgendada: dataRetorno,
      });
      addToast('Transferência de titularidade registrada com sucesso.', 'success');
      onConfirmado();
      onClose();
    } catch (error: any) {
      console.error('Error registering ancora transfer', error);
      addToast(error?.message || 'Erro ao registrar a transferência.', 'error');
    } finally {
      setSalvando(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-700 shrink-0">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white font-poppins">
            Transferência de Titularidade — {ug.nome}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X size={20} />
          </button>
        </div>

        <div className="px-6 pt-4 shrink-0">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
            Etapa {step + 1} de {STEPS.length} — {STEPS[step]}
          </p>
          <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-1.5">
            <div
              className="bg-primary h-1.5 rounded-full transition-all"
              style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
            />
          </div>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {step === 0 && (
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  value={termo}
                  onChange={e => setTermo(e.target.value)}
                  placeholder="Buscar UC por número ou nome do titular..."
                  className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg pl-9 pr-3 py-2"
                />
                {buscando && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400" size={16} />}
              </div>
              {resultados.length > 0 && (
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-100 dark:divide-gray-700 max-h-56 overflow-y-auto">
                  {resultados.map(uc => (
                    <button
                      key={uc.id}
                      onClick={() => setUcDestino(uc)}
                      className={`w-full text-left p-3 text-sm hover:bg-gray-50 dark:hover:bg-gray-900 ${
                        ucDestino?.id === uc.id ? 'bg-primary/5 border-l-2 border-primary' : ''
                      }`}
                    >
                      <p className="font-medium text-gray-900 dark:text-gray-100">{uc.numero_uc}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{uc.nome_cliente || 'Sem nome'}</p>
                    </button>
                  ))}
                </div>
              )}
              {ucDestino && (
                <p className="text-sm text-emerald-600 dark:text-emerald-400">
                  UC selecionada: {ucDestino.numero_uc} — {ucDestino.nome_cliente || 'Sem nome'}
                </p>
              )}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Montante a transferir (kWh)</label>
              <input
                type="number"
                step="0.01"
                min={0}
                value={montanteKwh}
                onChange={e => setMontanteKwh(e.target.value)}
                placeholder="0,00"
                className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Saldo Âncora disponível na UG: {saldoDisponivelKwh.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} kWh
                (já descontando transferências ativas)
              </p>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Data de retorno da titularidade
              </label>
              <input
                type="date"
                value={dataRetorno}
                onChange={e => setDataRetorno(e.target.value)}
                className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2"
              />

              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <button
                    onClick={() => {
                      if (mesCalendario === 0) {
                        setMesCalendario(11);
                        setAnoCalendario(a => a - 1);
                      } else setMesCalendario(m => m - 1);
                    }}
                    className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">{nomeMes}</p>
                  <button
                    onClick={() => {
                      if (mesCalendario === 11) {
                        setMesCalendario(0);
                        setAnoCalendario(a => a + 1);
                      } else setMesCalendario(m => m + 1);
                    }}
                    className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
                <div className="grid grid-cols-7 gap-1 text-center text-xs">
                  {DIAS_SEMANA.map((d, i) => (
                    <span key={i} className="text-gray-400 font-medium py-1">
                      {d}
                    </span>
                  ))}
                  {gradeDoMes.map(({ date, foraDoMes }) => {
                    const iso = dataParaIso(date);
                    const status = classificarDiaLeitura(date, diaLeituraPadrao);
                    const selecionado = dataRetorno === iso;
                    return (
                      <button
                        key={iso}
                        type="button"
                        onClick={() => setDataRetorno(iso)}
                        title={
                          status === 'leitura'
                            ? 'Dia de leitura da distribuidora'
                            : status === 'risco'
                              ? 'Próximo ao dia de leitura — risco de a distribuidora processar no ciclo errado'
                              : undefined
                        }
                        className={`py-1.5 rounded-md transition-colors ${foraDoMes ? 'opacity-30' : ''} ${
                          selecionado
                            ? 'ring-2 ring-primary font-semibold'
                            : status === 'leitura'
                              ? 'bg-red-500 text-white hover:bg-red-600'
                              : status === 'risco'
                                ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-900/50'
                                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        {date.getDate()}
                      </button>
                    );
                  })}
                </div>
                <div className="flex items-center gap-3 mt-2 text-[11px] text-gray-400">
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-red-500" /> Dia de leitura
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-amber-300" /> Risco (±3 dias)
                  </span>
                </div>
              </div>

              {statusDataEscolhida !== 'normal' && (
                <p className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded-lg p-2.5">
                  <AlertTriangle size={14} className="shrink-0" />
                  Data escolhida está {statusDataEscolhida === 'leitura' ? 'no dia' : 'perto do dia'} de leitura da
                  distribuidora — risco de o retorno ser processado no ciclo errado.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-between gap-3 p-6 border-t border-gray-100 dark:border-gray-700 shrink-0">
          <button
            onClick={() => (step === 0 ? onClose() : setStep(s => s - 1))}
            disabled={salvando}
            className="px-4 py-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-60"
          >
            {step === 0 ? 'Cancelar' : 'Voltar'}
          </button>
          {step < STEPS.length - 1 ? (
            <button
              onClick={() => setStep(s => s + 1)}
              disabled={!podeAvancar}
              className="px-5 py-2 rounded-lg bg-primary text-white font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Avançar
            </button>
          ) : (
            <button
              onClick={handleConfirmar}
              disabled={salvando || !dataRetorno}
              className="px-5 py-2 rounded-lg bg-primary text-white font-medium hover:opacity-90 disabled:opacity-60 flex items-center gap-2"
            >
              {salvando && <Loader2 size={16} className="animate-spin" />}
              Confirmar
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TransferenciaTitularidadeWizard;
